import { AxiosInstance } from 'axios';
import { stringify } from 'query-string';
import { PERSONNEL_API_ENDPOINTS } from './endpoints';
import {
  ApiEmployeeListRequest,
  ApiEmployeeGetRequest,
  ApiEmployeeCreateRequest,
  ApiEmployeeUpdateRequest,
  ApiEmployeeDeleteRequest,
  EmployeeApiResponse,
  EmployeeDetailSuccessResponse,
  EmployeeCreateSuccessResponse,
  EmployeeUpdateResponse,
  NoContentSuccessResponse,
  EmployeeHttpClientType,
} from './types/index';
import {
  PinGenerateSuccessResponse,
  PinValidateSuccessResponse,
  PinValidateRequest,
} from './types/EmployeeHttpClientType';
import { ApiEmployeeBulkDeleteRequest } from './types/EmployeeApiRequest';

export const createEmployeeHttpClient = (props: { httpClient: AxiosInstance }): EmployeeHttpClientType => {
  const { httpClient } = props;
  const fetchUsers = async (request: ApiEmployeeListRequest): Promise<EmployeeApiResponse> => {
    const response = await httpClient.get<EmployeeApiResponse>(
      `${PERSONNEL_API_ENDPOINTS.EMPLOYEES.LIST}?${stringify(request, {
        arrayFormat: 'comma',
        encode: false,
        skipNull: true,
      })}`,
    );
    return response.data;
  };

  const getUser = async (request: ApiEmployeeGetRequest): Promise<EmployeeDetailSuccessResponse> => {
    const url = PERSONNEL_API_ENDPOINTS.EMPLOYEES.GET.replace('{userUuid}', request.userUuid);
    const query = stringify(
      request.includeAssignedStores !== undefined ? { includeAssignedStores: request.includeAssignedStores } : {},
    );
    const response = await httpClient.get<EmployeeDetailSuccessResponse>(query ? `${url}?${query}` : url);
    return response.data;
  };

  const createUserV2 = async (request: ApiEmployeeCreateRequest): Promise<EmployeeCreateSuccessResponse> => {
    const response = await httpClient.post<EmployeeCreateSuccessResponse>(
      PERSONNEL_API_ENDPOINTS.EMPLOYEES.CREATE,
      request,
    );
    return response.data;
  };

  const updateUsers = async (request: ApiEmployeeUpdateRequest): Promise<EmployeeUpdateResponse> => {
    const url = PERSONNEL_API_ENDPOINTS.EMPLOYEES.UPDATE.replace('{userUuid}', request.userUuid);
    const { userUuid, ...body } = request;
    const response = await httpClient.patch<EmployeeUpdateResponse>(url, body);
    return response.data;
  };

  const deleteUserV2 = async (request: ApiEmployeeDeleteRequest): Promise<NoContentSuccessResponse> => {
    const url = PERSONNEL_API_ENDPOINTS.EMPLOYEES.DELETE.replace('{userUuid}', request.userUuid);
    const response = await httpClient.delete<NoContentSuccessResponse>(url);
    return response.data;
  };

  const generatePin = async (): Promise<PinGenerateSuccessResponse> => {
    const response = await httpClient.get<{ data: PinGenerateSuccessResponse; datetime: string }>(
      PERSONNEL_API_ENDPOINTS.EMPLOYEES.PIN_GENERATE,
    );
    return response.data.data;
  };

  const validatePin = async (request: PinValidateRequest): Promise<PinValidateSuccessResponse> => {
    const response = await httpClient.post<{ data: PinValidateSuccessResponse; datetime: string }>(
      PERSONNEL_API_ENDPOINTS.EMPLOYEES.PIN_VALIDATE,
      request,
    );
    return response.data.data;
  };

  const bulkDeleteUsers = async (request: ApiEmployeeBulkDeleteRequest): Promise<NoContentSuccessResponse> => {
    const response = await httpClient.post<NoContentSuccessResponse>(
      PERSONNEL_API_ENDPOINTS.EMPLOYEES.BULK_DELETE,
      request,
    );
    return response.data;
  };

  return {
    fetchUsers,
    getUser,
    createUserV2,
    updateUsers,
    deleteUserV2,
    generatePin,
    validatePin,
    bulkDeleteUsers,
  };
};
